<script>
/* global axios */
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';

// WhatsApp-via-Zernio provider (Meta Cloud API / WhatsApp Business Platform),
// rendered inside Whatsapp.vue when the "Zernio" provider is picked. Mirrors the
// Instagram/Messenger Zernio connect (Zernio.vue) but for WhatsApp, which uses
// Meta's Embedded Signup (a non-headless popup) and needs no page/phone picker
// for a single-number WABA: create a pending inbox → open the authUrl → the
// customer completes Embedded Signup on Meta's page → the popup returns to our
// /zernio/connected landing (postMessage) and/or the poll binds the connected
// account. The whole provider is gated OFF by default (WHATSAPP_ZERNIO_ON).
const STATUS = Object.freeze({
  PENDING: 'pending',
  CONNECTED: 'connected',
  ERROR: 'error',
});
const POLL_INTERVAL_MS = 4000;

export default {
  components: { NextButton },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      step: 'form', // 'form' | 'connecting'
      inboxName: '',
      createdInboxId: null,
      connectionStatus: STATUS.PENDING,
      connectError: null,
      pollTimer: null,
      authWindow: null,
      authUrl: null,
      isSubmitting: false,
    };
  },
  validations: {
    inboxName: { required },
  },
  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
    isConnecting() {
      return this.step === 'connecting';
    },
    isError() {
      return this.connectionStatus === STATUS.ERROR;
    },
  },
  mounted() {
    // The connect popup returns to our own /zernio/connected page, which posts
    // this message — bind + close immediately instead of waiting for the poll.
    window.addEventListener('message', this.onConnectMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onConnectMessage);
    this.stopPolling();
  },
  methods: {
    onConnectMessage(event) {
      const data = event?.data;
      if (data?.source !== 'metrica-social-connect') return;
      // WhatsApp has no page/phone picker for a single-number WABA — a
      // 'connected' message (or the poll) is enough to bind.
      if (data.status === 'connected') {
        this.checkStatus();
      }
    },
    async startConnect() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      // Open the popup synchronously (inside the user gesture) so it isn't
      // blocked; point it at the authUrl once the backend returns it.
      const popup = window.open(
        '',
        'zernio_whatsapp_connect',
        'width=620,height=760'
      );
      this.authWindow = popup;
      this.isSubmitting = true;
      try {
        const inbox = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName?.trim(),
          channel: { type: 'zernio', platform: 'whatsapp' },
        });
        this.createdInboxId = inbox.id;

        const { data } = await axios.post(
          `/api/v1/accounts/${this.accountId}/inboxes/${inbox.id}/zernio_connect`
        );
        this.authUrl = data.auth_url;
        if (popup && !popup.closed) {
          popup.location.href = this.authUrl;
        }
        this.step = 'connecting';
        this.startPolling();
      } catch (error) {
        if (popup) {
          try {
            popup.close();
          } catch (e) {
            // cross-origin; ignore
          }
        }
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.ERROR_MESSAGE')
        );
      } finally {
        this.isSubmitting = false;
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(this.checkStatus, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async checkStatus() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.createdInboxId}/zernio_status`
        );
        if (data.status === STATUS.CONNECTED) {
          this.onConnected();
        } else if (data.status === STATUS.ERROR) {
          this.connectionStatus = STATUS.ERROR;
          this.connectError = data.error;
          this.stopPolling();
        }
      } catch (e) {
        // transient error — keep polling
      }
    },
    onConnected() {
      this.connectionStatus = STATUS.CONNECTED;
      this.stopPolling();
      try {
        this.authWindow?.close();
      } catch (e) {
        // cross-origin; ignore
      }
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: this.createdInboxId },
      });
    },
    retry() {
      this.connectionStatus = STATUS.PENDING;
      this.connectError = null;
      if (this.authUrl) {
        this.authWindow = window.open(
          this.authUrl,
          'zernio_whatsapp_connect',
          'width=620,height=760'
        );
      }
      this.startPolling();
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm leading-relaxed text-n-slate-11">
      {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.DESC') }}
    </p>

    <div
      v-if="!isConnecting"
      class="p-4 rounded-xl border border-n-weak bg-n-alpha-2"
    >
      <h3 class="mb-2 text-sm font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.WHATSAPP_ABOUT.TITLE') }}
      </h3>
      <ul class="space-y-1.5 text-sm list-disc list-inside text-n-slate-11">
        <li>
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.WHATSAPP_ABOUT.OFFICIAL') }}
        </li>
        <li>
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.WHATSAPP_ABOUT.COEXISTENCE') }}
        </li>
        <li>
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.WHATSAPP_ABOUT.META_STEP') }}
        </li>
        <li>
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.WHATSAPP_ABOUT.DATA') }}
        </li>
        <li>
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.WHATSAPP_ABOUT.SETUP_TIME') }}
        </li>
      </ul>
    </div>

    <div
      v-if="!isConnecting"
      class="p-4 text-sm border rounded-xl border-n-amber-6 bg-n-amber-3 text-n-amber-11"
    >
      {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.WHATSAPP_WARNING') }}
    </div>

    <form
      v-if="!isConnecting"
      class="flex flex-col"
      @submit.prevent="startConnect()"
    >
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CHANNEL_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
          "
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CHANNEL_NAME.ERROR') }}
        </span>
      </label>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isSubmitting"
          type="submit"
          solid
          blue
          :label="
            $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECT_BUTTON', {
              platform: 'WhatsApp',
            })
          "
        />
      </div>
    </form>

    <div v-else class="flex flex-col items-start gap-3">
      <template v-if="isError">
        <p class="text-n-ruby-11 text-sm">
          {{
            connectError || $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECTING.ERROR')
          }}
        </p>
        <NextButton
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECTING.RETRY')"
          @click="retry"
        />
      </template>
      <template v-else>
        <p class="font-medium">
          {{
            $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECTING.TITLE', {
              platform: 'WhatsApp',
            })
          }}
        </p>
        <p class="text-n-slate-11 text-sm">
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECTING.DESC') }}
        </p>
        <a
          v-if="authUrl"
          :href="authUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-n-blue-11 underline text-sm"
        >
          {{ $t('INBOX_MGMT.ADD.ZERNIO_CHANNEL.CONNECTING.REOPEN') }}
        </a>
      </template>
    </div>
  </div>
</template>
